import { useEffect, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Wand2 } from 'lucide-react';

import { apiClient } from '@/api/client';
import { ApiError } from '@/api/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { queryKeys } from '@/query/keys';

/** Tenant-scoped SystemConfig key holding the PFQ application URL (mirrors the backend constant). */
const CONFIG_KEY = 'ppq.application-url';
const CONFIG_QUERY_KEY = ['system-config', CONFIG_KEY] as const;

/**
 * Admin control for the PFQ application URL. Reads/writes the tenant-scoped {@code ppq.application-url}
 * SystemConfig, and offers a "Generate" action that asks the backend to discover the URL from the
 * service registry (works once the PFQ app registers with Eureka in ptv2 mode).
 */
export function PpqApplicationUrlSettings(): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [value, setValue] = useState('');
  const [generateNote, setGenerateNote] = useState<string | null>(null);

  const currentQuery = useQuery<string>({
    queryKey: CONFIG_QUERY_KEY,
    queryFn: async ({ signal }) => {
      try {
        const config = await apiClient.getConfig(CONFIG_KEY, { signal });
        return config.value ?? '';
      } catch (error) {
        // A missing key is the normal "not configured yet" state, not an error.
        if (error instanceof ApiError && error.status === 404) {
          return '';
        }
        throw error;
      }
    },
  });

  // Seed the input once the stored value loads (and on refetch after save).
  useEffect(() => {
    if (currentQuery.isSuccess) {
      setValue(currentQuery.data);
    }
  }, [currentQuery.isSuccess, currentQuery.data]);

  const save = useMutation({
    meta: { successMessage: 'success.toast.title.ppq_url_saved' },
    mutationFn: async (next: string) => {
      await apiClient.upsertConfig(CONFIG_KEY, { value: next.trim() });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONFIG_QUERY_KEY }).catch(() => undefined);
      // The send-gate flag depends on this value.
      queryClient.invalidateQueries({ queryKey: queryKeys.questionnaireConfig }).catch(() => undefined);
      // Same SystemConfig store the Admin Configs table lists — keep it fresh.
      queryClient.invalidateQueries({ queryKey: ['admin', 'configs'] }).catch(() => undefined);
    },
  });

  const generate = useMutation({
    mutationFn: async () => (await apiClient.getQuestionnaireSuggestedUrl()).url,
    onSuccess: (url) => {
      if (url !== null && url.length > 0) {
        setValue(url);
        setGenerateNote(null);
      } else {
        setGenerateNote(t('routes.admin.questionnaires.applicationUrl.generateEmpty'));
      }
    },
  });

  return (
    <section aria-labelledby="admin-questionnaire-url-heading" className="space-y-4">
      <div>
        <h2 id="admin-questionnaire-url-heading" className="text-base font-semibold text-foreground">
          {t('routes.admin.questionnaires.applicationUrl.heading')}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('routes.admin.questionnaires.applicationUrl.subheading')}
        </p>
      </div>

      <div className="flex max-w-2xl flex-wrap items-end gap-2">
        <label className="min-w-[16rem] flex-1 space-y-1 text-xs font-medium">
          {t('routes.admin.questionnaires.applicationUrl.label')}
          <Input
            type="url"
            value={value}
            disabled={currentQuery.isPending}
            placeholder={t('routes.admin.questionnaires.applicationUrl.placeholder')}
            onChange={(event) => {
              setValue(event.target.value);
            }}
            className="h-9 text-sm"
          />
        </label>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            generate.mutate();
          }}
          loading={generate.isPending}
        >
          <Wand2 className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
          {t('routes.admin.questionnaires.applicationUrl.generate')}
        </Button>
        <Button
          type="button"
          onClick={() => {
            save.mutate(value);
          }}
          loading={save.isPending}
          disabled={currentQuery.isPending || value.trim() === currentQuery.data}
        >
          {t('routes.admin.questionnaires.applicationUrl.save')}
        </Button>
      </div>

      {generateNote !== null && <p className="text-xs text-muted-foreground">{generateNote}</p>}
      {save.isError && (
        <p className="text-xs text-destructive">
          {t('routes.admin.questionnaires.applicationUrl.saveError')}
        </p>
      )}
    </section>
  );
}
